import { ICartEntry, ISanityImage } from '@rbi-ctg/menu';
import {
  CartServiceMode,
  IHydrateCartMutation,
  IMenuSelectionInput,
  ServiceMode,
} from 'generated/graphql-gateway';
import { ModifiersSelections, SlotsSelections } from 'pages/menu/components/product-wizard/types';
import { IMinimalCartEntry } from 'state/global-state/models/ordering/cart/cart.types';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { isItem, mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { CartEntryType } from 'utils/cart/types';

const REWARD_COMBO_MARKER = 'loyalty';

export const getCartEntryId = ({ _id, sanityId }: ICartEntry): string =>
  sanityId?.trim().length ? sanityId : _id;

export function mapLegacyToMinimalCartEntries(
  legacyCartEntries: ICartEntry[]
): IMinimalCartEntry[] {
  return legacyCartEntries.flatMap(legacyCartEntry => {
    const processedChildren = mapLegacyToMinimalCartEntries(legacyCartEntry.children);
    const processedMenuSelections = mapLegacyToMenuSelections(legacyCartEntry.children);

    return [
      {
        id: getCartEntryId(legacyCartEntry),
        quantity: legacyCartEntry.quantity,
        name: legacyCartEntry.name,
        image: legacyCartEntry.image! as ISanityImage,
        url: legacyCartEntry.url,
        menuSelections: processedMenuSelections,
        children: processedChildren,
        isExtra: legacyCartEntry.isExtra,
        isDonation: legacyCartEntry.isDonation,
      },
    ];
  });
}

export function mapLegacyToMenuSelections(legacyCartEntries: ICartEntry[]): IMenuSelectionInput[] {
  return legacyCartEntries.flatMap(legacyCartEntry => {
    if (
      legacyCartEntry.type === CartEntryType.itemOption ||
      legacyCartEntry.type === CartEntryType.comboSlot ||
      legacyCartEntry.isDummyItem ||
      isDummyItem(legacyCartEntry)
    ) {
      return mapLegacyToMenuSelections(legacyCartEntry.children);
    }

    const nestedMenuSelections = mapLegacyToMenuSelections(legacyCartEntry.children);

    return [
      {
        id: getCartEntryId(legacyCartEntry),
        quantity: legacyCartEntry.quantity,
        menuSelections: nestedMenuSelections,
      },
    ];
  });
}

export function mapHydratedCart(
  hydratedCartEntries: IHydrateCartMutation['hydrateCart']['cartEntries'],
  minimalCartEntriesLookup: Map<string, IMinimalCartEntry>
): ICartEntry[] {
  return (hydratedCartEntries ?? []).map(hydratedCartEntry => {
    const minimalCartEntry = minimalCartEntriesLookup.get(hydratedCartEntry.sanityId)!;
    return {
      lineId: minimalCartEntry?.cartId ?? hydratedCartEntry.lineId,
      cartId: minimalCartEntry?.cartId ?? hydratedCartEntry.lineId,
      _id: hydratedCartEntry.sanityId,
      sanityId: hydratedCartEntry.sanityId,
      name: hydratedCartEntry.name,
      type: (hydratedCartEntry.type as unknown) as ICartEntry['type'],
      quantity: hydratedCartEntry.quantity ?? 1,
      pickerSelections: {},
      posIdsConfiguration: hydratedCartEntry.posIdsConfiguration,
      discountType: hydratedCartEntry.discountType,
      vendorConfigs: hydratedCartEntry.vendorConfigs,
      url: minimalCartEntry?.url ?? '',
      image: minimalCartEntry?.image,
      isExtra: minimalCartEntry?.isExtra,
      isDonation: minimalCartEntry?.isDonation,
      children: mapHydratedCart(
        hydratedCartEntry.children as IHydrateCartMutation['hydrateCart']['cartEntries'],
        minimalCartEntriesLookup
      ),
    } as ICartEntry;
  });
}

export function mapServiceModeToCartServiceMode(serviceMode: ServiceMode): CartServiceMode {
  // This is the same as a switch statement, becase the two enums are the same
  return (serviceMode as unknown) as CartServiceMode;
}

export function buildMinimalCartEntriesLookup(
  minimalCartEntries: IMinimalCartEntry[]
): Map<string, IMinimalCartEntry> {
  const minimalCartEntriesLookup = new Map<string, IMinimalCartEntry>();

  function addEntriesToLookup(entries: IMinimalCartEntry[]) {
    entries.forEach(entry => {
      minimalCartEntriesLookup.set(entry.id, entry);
      if (entry.children && entry.children.length > 0) {
        addEntriesToLookup(entry.children);
      }
    });
  }

  addEntriesToLookup(minimalCartEntries);

  return minimalCartEntriesLookup;
}

export function findMatchingCartEntry(
  minimalCartEntries: IMinimalCartEntry[],
  cartEntry: ICartEntry
): IMinimalCartEntry | undefined {
  return minimalCartEntries.find(
    x => x.cartId === cartEntry.cartId || x.id === cartEntry.sanityId || x.id === cartEntry._id
  );
}

export function findRewardBenefitFromHydratedCartEntry(
  cartEntry: ICartEntry
): ICartEntry | undefined {
  if (!isHydratedReward(cartEntry)) {
    return undefined;
  }

  const recursiveFind = (cartEntries: ICartEntry[] = []): ICartEntry | undefined => {
    for (const child of cartEntries) {
      if (child.cartId !== REWARD_COMBO_MARKER) {
        return child;
      }

      const foundBenefit = recursiveFind(child.children);
      if (foundBenefit) {
        return foundBenefit;
      }
    }

    return undefined;
  };

  return recursiveFind(cartEntry.children);
}

export function isDummyItem(item: { name: string } | string): boolean {
  const name = typeof item === 'string' ? item : item.name;
  return ['combo sides', 'combo item', 'mix n match', "mix n' match"].includes(
    name?.toLowerCase().trim()
  );
}

export function mapSelectedProductToMenuSelections(
  selectedProduct: ILocalizedMenuEntry,
  quantity: number,
  slotsSelections: SlotsSelections
): IMenuSelectionInput[] {
  return [
    {
      id: selectedProduct.id,
      quantity,
      menuSelections: mapSlotsSelectionsToMenuSelections(slotsSelections, isItem(selectedProduct)),
    },
  ];
}

export function mapSlotsSelectionsToMenuSelections(
  slotsSelections: SlotsSelections,
  isItem: boolean
): IMenuSelectionInput[] {
  return Object.values(slotsSelections)
    .filter(slotSelection => !!slotSelection)
    .flatMap(slotSelection => {
      const modifiersSelections = mapModifiersToMenuSelections(slotSelection!.modifiers ?? {});
      return isItem
        ? modifiersSelections
        : [
            {
              id: slotSelection!.itemId,
              quantity: 1,
              menuSelections: modifiersSelections,
            },
          ];
    });
}

export function mapModifiersToMenuSelections(
  modifiersSelections: ModifiersSelections
): IMenuSelectionInput[] {
  return Object.values(modifiersSelections).flatMap(multipliersSelections =>
    Object.entries(multipliersSelections).map(([multiplierKey, quantity]) => ({
      id: multiplierKey,
      quantity,
    }))
  );
}

export function mapMenuSelectionsToMinimalCartEntry(
  menuSelections: IMenuSelectionInput[],
  options: {
    menu: IndexedMenu;
    posServiceMode: 'delivery' | 'pickup';
    url: string;
  }
): IMinimalCartEntry[] {
  return menuSelections.map(menuSelection => {
    const menuEntry = options.menu.find(menuSelection.id);
    return {
      ...menuSelection,
      name: menuEntry?.name?.locale ?? '',
      image: mapImageResourceToSanityImage(
        menuEntry?.image?.resource ?? '',
        menuEntry?.image?.altText?.locale ?? ''
      ),
      url: options.url,
      children: mapMenuSelectionsToMinimalCartEntry(
        (menuSelection.menuSelections as IMenuSelectionInput[]) ?? [],
        options
      ),
      ...(menuEntry &&
        '__parentLoyaltyEntityId' in menuEntry && {
          id: menuEntry.__parentLoyaltyEntityId as string,
          referenceId: menuEntry.id,
        }),
    };
  });
}

export function isHydratedReward(cartEntry: ICartEntry): boolean {
  return cartEntry.children.every(child => child.cartId === REWARD_COMBO_MARKER);
}

export function getHydratedEntityId(entryId: string, parentId: string) {
  const prefix = `${parentId}$`;
  // Hydration v2
  if (entryId.startsWith(prefix)) {
    return entryId;
  }
  // Hydration v1
  return `${prefix}${entryId}`;
}
